import React, { useEffect } from "react";
import ReactSelect from "react-select";
import { useHplsStore } from "./store/hplsStore.js";
import { Form, Field, useField, useForm } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { DatePicker } from "./datePicker/DatePicker.js";
import { today, getLocalTimeZone } from "@internationalized/date";
import { getDaysInMonth } from "date-fns";
import {
  Heading,
  Box,
  Flex,
  Stack,
  //Badge,
  Button,
  ButtonGroup,
  Center,
  Divider,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Checkbox,
  Select,
  Text,
  Textarea,
  Alert,
  AlertTitle,
  AlertDescription,
  OrderedList,
  ListItem,
  Radio,
  RadioGroup,
  SimpleGrid,
  Switch,
  Container,
} from "@chakra-ui/react";
import { WarningIcon, InfoIcon } from "@chakra-ui/icons";
import validate from "./validate";
import HPLSInfoAccordion from "./HPLSInfoAccordion.js";
import HPLSFormHeader from "./HPLSFormHeader.js";
import countryNames from "./CountryNames";
import incomeSources from "./IncomeSources";
import {
  PhoneDescription,
  WelfareAgency,
  AppointmentTimes,
  HousingStatus,
  AboriginalTorresStrait,
  CalendarMonths,
  GenderIdentity,
  PreferredLanguage,
} from "./OptionData";
import { LEGAL_ISSUES } from "./LegalIssues.ts";
import {
  daysInSelectedMonth,
  dobYears,
  isValidFullDate,
} from "./DateSelectorData";
import ErrorBoundary from "./ErrorBoundary.js";

//sizes
const containerMW = "800px"; //'800px'
const controlWidth = {
  xs: "8em",
  sm: "12em",
  lg: "18em",
  xl: "24em",
};

function App() {
  //HPLS store variables
  //initial date values for birthdates
  const yearNow = useHplsStore((state) => state.yearNow);
  const ageRange = useHplsStore((state) => state.ageRange);

  //renderToggle is used to trigger form validation and update the user with feedback
  const renderToggle = useHplsStore((state) => state.renderToggle);
  const updateRenderToggle = useHplsStore((state) => state.updateRenderToggle);

  //checkButtonClicked state is used to trigger validation before mandatory fields are filled out if renderToggle is true
  const checkButtonClicked = useHplsStore((state) => state.checkButtonClicked);
  const updateCheckButtonClicked = useHplsStore(
    (state) => state.updateCheckButtonClicked
  );

  //mandatoryFieldsArray is used to track mandatory fields in the form
  const mandatoryFields = useHplsStore((state) => state.mandatoryFields);

  const dateInputData = useHplsStore((state) => state.dateInputData);
  const updateDateInputData = useHplsStore(
    (state) => state.updateDateInputData
  );

  const problemsData = useHplsStore((state) => state.problemsData);
  const updateProblemsData = useHplsStore((state) => state.updateProblemsData);

  const AS_FORM_FIELDS = useHplsStore((state) => state.AS_FORM_FIELDS);

  //transpile and track legal issues selected by user
  const handleProblemsData = (data) => {
    if (data !== null || data !== undefined || data.length > 0) {
      return updateProblemsData(data.map((item) => item.value));
    } else {
      return updateProblemsData([]);
    }
  };

  const handleResetButtonClicked = () => {
    //reset form
    useForm.values = initialData;
    updateCheckButtonClicked(false);
    updateRenderToggle(false);
    //reset date input data
    updateDateInputData([
      {
        id: "clientDOB",
        day: "Day",
        month: "Month",
        year: "Year",
        dayCount: null,
        is_full_date: false,
        dateString: null, //full date string parsed as ActionStep expects it
        text: null,
      },
      {
        id: "otherSideDOB",
        day: "Day",
        month: "Month",
        year: "Year",
        dayCount: null,
        is_full_date: false,
        dateString: null,
        text: null,
      },
      {
        id: "HplsClinicApptDate",
        day: "Day",
        month: "Month",
        year: "Year",
        dayCount: null,
        is_full_date: false,
        dateString: null,
        text: null,
      },
    ]);
    //reset problems data
    updateProblemsData([]);
    //reset form instructions
    formInstructions.report = { title: "", info: "" };
    return;
  };

  const getDirtyMandatoryFields = (
    renderToggle,
    mandatoryFields,
    dirtyFields
  ) => {
    //extract keys from dirty fields
    const dirtyKeys = Object.keys(dirtyFields);

    if (dirtyKeys.length > 0) {
      const set = new Set(mandatoryFields);
      const hasCommonItem = dirtyKeys.some((item) => set.has(item));
      return hasCommonItem;
    } else {
      return false;
    }
  };

  const formDate = new Date();
  const formDateStr =
    formDate.getFullYear() +
    "-" +
    (formDate.getMonth() + 1) +
    "-" +
    formDate.getDate();

  var initialData = {
    organization_key: "piacco",
    dc118__ClientContact: formDateStr,
    dc118__HplsWebformSubmissionDate: formDateStr,
    uid: "79018c5388ea28d794d36b6d86633515c716edf4",
    isAgency: false,
    dc118__ReferralOrg: null,
    dc118__WelfareAgencyV2: "Not from a HPLS Host Agency",
    dc118__HplsClinicApptDateAndTime: "",
    HplsClinicApptDate: null,
    p27__date_of_birth: null,
    clientDOB: null,
    clientDOB_year: "Year",
    clientDOB_month: "Month",
    clientDOB_day: "Day",
    interpreter_required: false,
    dc217__ReqInterpreter: "No",
    p62__date_of_birth: null,
    p62__company_flag: null,
    p62__company_name: null,
    p62__first_name: null,
    p62__middle_name: null,
    wasReferred: false,
    p27__first_name: null,
    p27__middle_name: null,
    p27__last_name: null,
    dc217__Housing: null,
    p27__address_line_1: null,
    p27__address_line_2: null,
    p27__city: null,
    p27__post_code: null,
    p27__e_mail: null,
    p27__phone1: null,
    p27__phone1_description: null,
    dc118__Caseworker: null,
    dc118__CaseworkerConsent: "Yes",
    dc118__Problems: null,
    dc118__InstructionsMatterDescription: null,
    dc118__CriminalMatter: null,
    otherDisputeParties: null,
    dc118__WebformOtherParties: null,
    p27__64__CountryOfBirth: "(Please select)",
    p27__class_primary_language: "(Please select)",
    p27__gender: null,
    p27__class_aboriginal_status: null,
    safe_contact_list: null,
    disabilities_list: null,
    p27__45__Disability1: "(Please select)",
    p27__44__Disability2: "(Please select)",
    p27__46__Disability3: "(Please select)",
    p27__52__Disability4: "(Please select)",
    dc217__IncomeSource: "(Please select)",
  };

  var formInstructions = {}; //instructions for user
  formInstructions.report = { title: "Init", info: "Placeholder" };

  //called by date selector components to validate and update date input data
  const handleDateInputData = (id, day, month, year) => {
    //Check day count for month. set to 'Day' if over limit
    const dayCount = getDaysInMonth(new Date(year, month - 1, 1));
    if (day > dayCount || isNaN(day)) {
      day = "Day";
    }

    //Check day for valid values
    if (day < 1 || day > 31 || isNaN(day)) {
      day = "Day";
    }
    if (month < 1 || month > 12 || isNaN(month)) {
      month = "Month";
    }
    if (year < 1900 || year > yearNow || isNaN(year)) {
      year = "Year";
    }

    //if day month or year is null, set is_full_date false, otherwise true
    const is_full_date = isValidFullDate(year, month, day);

    //return new date input data
    const newDateInputData = dateInputData.map((item) => {
      if (item.id === id) {
        if (is_full_date) {
          updateRenderToggle(true);
        }
        return {
          ...item,
          day: day,
          month: month,
          year: year,
          is_full_date: is_full_date,
        };
      } else {
        return item;
      }
    });
    return updateDateInputData(newDateInputData);
  };

  //error message bahaviour
  const showError = (form, fieldName, renderToggle, isRequired) => {
    const formState = form.getState();

    //TO DO:
    // fetch only used fields from formState
    // check if field is dirty and required could be done here
    // move some of the logic relating to renderToggle here?

    if (formState.errors[fieldName] && renderToggle && isRequired) {
      return (
        <Error name={fieldName} show={renderToggle} isRequired={isRequired} />
      );
    } else {
      return;
    }
  };

  const showDateWarningColor = (d, m, y) => {
    let color;
    if (d === "Day" || m === "Month" || y === "Year") {
      color = "piacred.500";
    } else {
      color = "piacgray.700";
    }
    return color;
  };

  // Update values from user input and report status back to user
  const ProcessFormVars = () => {
    const form = useForm();
    const formState = form.getState();

    let values = formState.values;
    let modified = formState.modified;
    let errors = formState.errors;
    let fieldIsActive = formState.active;
    let dirtyFields = formState.dirtyFields;

    const hasDirtyMandatoryFields = getDirtyMandatoryFields(
      renderToggle,
      mandatoryFields,
      dirtyFields
    );

    // Move state updates to useEffect
    useEffect(() => {
      if (formState.dirty && hasDirtyMandatoryFields && !fieldIsActive) {
        updateRenderToggle(true);
      }
    }, [formState.dirty, hasDirtyMandatoryFields, fieldIsActive]);

    // Handle problems data updates in a separate effect
    useEffect(() => {
      if (
        modified.dc118__Problems &&
        values.dc118__Problems !== null &&
        values.dc118__Problems.length > 0 &&
        values.dc118__Problems[0].value !== undefined
      ) {
        handleProblemsData(values.dc118__Problems);
      }
    }, [modified.dc118__Problems, values.dc118__Problems]);

    function updateFormTrack(values, fs, mf, of, mfe, ofe, cbc) {
      validate(values);
      return [fs, mf, of, mfe, ofe, cbc];
    }

    values.formTrack = updateFormTrack(
      values,
      formState.pristine,
      errors.mandatoryFieldsCount,
      errors.optionalFieldsCount,
      errors.mandatoryFieldErrorCount,
      errors.optionalFieldErrorCount,
      checkButtonClicked
    );

    //console.log('FORMSTATE: ',formState)

    //Compile AS compatible client DOB string
    if (
      isValidFullDate(
        values.clientDOB_year,
        values.clientDOB_month,
        values.clientDOB_day
      ) &&
      values.clientDOB_day !== "Day" &&
      values.clientDOB_month !== "Month" &&
      values.clientDOB_year !== "Year"
    ) {
      values.p27__date_of_birth = `${values.clientDOB_year}-${values.clientDOB_month}-${values.clientDOB_day}`;
    } else {
      values.p27__date_of_birth = null;
    }

    //other side DOB
    if (
      isValidFullDate(
        values.otherSideDOB_year,
        values.otherSideDOB_month,
        values.otherSideDOB_day
      ) &&
      values.otherSideDOB_day !== "Day" &&
      values.otherSideDOB_month !== "Month" &&
      values.otherSideDOB_year !== "Year"
    ) {
      values.p62__date_of_birth = `${values.otherSideDOB_year}-${values.otherSideDOB_month}-${values.otherSideDOB_day}`;
    } else {
      values.p62__date_of_birth = null;
    }

    //Clear previously entered date if "individual" is not selected
    if (values.p62__company_flag !== "F") {
      delete values.p62__date_of_birth;
      delete values.otherSideDOB_day;
      delete values.otherSideDOB_month;
      delete values.otherSideDOB_year;
    }

    // HPLS clinic appointment date time
    // client request that date, time, or both can be entered. They have been made aware ofthe consequences of not entering both.
    // AS (the recipient platform) treats these values as text and does not validate them.
    // Date and time will be parsed separately and the results concatenated into a single field for AS
    // Date only "2021-09-30 " time only " 12:30", date and time "2021-09-30 12:30"

    function setApptDate(d) {
      let dStr = d.day;
      let mStr = d.month;

      if (dStr < 10) {
        dStr = "0" + dStr;
      }
      if (mStr < 10) {
        mStr = "0" + mStr;
      }

      return `${d.year}-${mStr}-${dStr}`;
    }

    //set text represemtation of appt date and time values for ActionStep
    if (modified.HplsClinicApptDate || modified.HplsClinicApptTime) {
      let HplsClinicApptDateStr = "";
      let HplsClinicApptTimeStr = "";
      if (values.HplsClinicApptDate !== null) {
        HplsClinicApptDateStr = setApptDate(values.HplsClinicApptDate);
      }

      if (
        values.HplsClinicApptTime !== undefined &&
        values.HplsClinicApptTime !== null
      ) {
        const re = /:| +/;
        const aTime = values.HplsClinicApptTime.split(re);
        let hour24 =
          aTime[2] === "PM" ? parseInt(aTime[0]) + 12 : parseInt(aTime[0]);
        //add leading zero to single digit hours
        if (hour24 < 10) {
          hour24 = "0" + hour24;
        }
        let minute = parseInt(aTime[1]);
        //add leading zero to single digit minutes
        if (minute < 10) {
          minute = "0" + minute;
        }
        HplsClinicApptTimeStr = `${hour24}:${minute}`;
      }
      values[
        "dc118__HplsClinicApptDateAndTime"
      ] = `${HplsClinicApptDateStr} ${HplsClinicApptTimeStr}`;
    }

    //transpile and set nonstandard variable names for AS multi selects. These will be in the final form instead of dc118__Problems and dc217__SafeContact
    //problem list
    values["dc118__Problem1[]"] = problemsData;

    //safe contact list
    if (modified.safe_contact_list && values.safe_contact_list !== null) {
      values["dc217__SafeContact[]"] = values.safe_contact_list;
    }
    //change true false to 1/0 for ActionStep in dc217__ReqInterpreter
    if (modified.interpreter_required) {
      values["dc217__ReqInterpreter"] = values.interpreter_required
        ? "Yes"
        : "No";
    }

    //transpile disablity list
    if (modified.disabilities_list && values.disabilities_list !== null) {
      //max number of accepted disabilities = 4
      let n =
        values.disabilities_list.length > 4
          ? 4
          : values.disabilities_list.length;

      //clear previous values
      for (let i = 0; i < 5; i++) {
        let key = "disabilities_list" + (i + 1);
        delete values[key];
      }

      //populate updated disability list
      //AS form expects p27__45__Disability1,p27__44__Disability2,p27__44__Disability2,p27__46__Disability3,p27__52__Disability4 to contain selection or redundant '(Please Select)' value

      for (let i = 0; i < n; i++) {
        if (values.disabilities_list !== null) {
          let disabilityFld = "";
          if (i === 0) {
            disabilityFld = "p27__45__Disability1";
          } else if (i === 1) {
            disabilityFld = "p27__44__Disability2";
          } else if (i === 2) {
            disabilityFld = "p27__46__Disability3";
          } else if (i === 3) {
            disabilityFld = "p27__52__Disability4";
          }

          let key = disabilityFld;
          let val = !values.disabilities_list[i]
            ? "(Please Select)"
            : values.disabilities_list[i];

          values[key] = val;
        }
      }
    }

    // Handle host agency v not
    if (!values.isAgency) {
      values.dc118__WelfareAgencyV2 = "Not from a HPLS Host Agency";
      if (values.dc118__ReferralOrg === "Host Agency") {
        //clear referral org if previously set
        values.dc118__ReferralOrg = null;
      }
    }

    if (values.isAgency) {
      values.dc118__ReferralOrg = "Host Agency";
      values.wasReferred = null;
    }

    if (!values.wasReferred && !values.isAgency) {
      values.dc118__ReferralOrg = null;
    }
  };
  const DisplayFormInstructions = () => {
    const form = useForm();
    const formState = form.getState();
    let values = formState.values;
    let errors = formState.errors;

    //user progress, feedback
    if (Object.keys(errors)) {
      //0-pristine, 1-totalMandatoryFieldsCount, 2-totalWarningFieldsCount, 3-invalidFieldsCount (mandatory), 4-optionalFieldsCount (optional)
      const formIsPristine = values.formTrack ? values.formTrack[0] : true;
      const mandatoryFieldCount = values.formTrack ? values.formTrack[1] : 0;
      //const optionalFieldCount = values.formTrack ? values.formTrack[2] : 0
      const mandatoryFieldErrorCount = values.formTrack
        ? values.formTrack[3]
        : 0; //totalErrorCount - optionalFieldErrorCount //remaining errors after warnings are removed. Used for information only
      const optionalFieldErrorCount = values.formTrack
        ? values.formTrack[4]
        : 0; //total warnings for fields which are not mandatory but must be valid

      if (!formIsPristine) {
        //Activate when the form has started to be filled out

        if (mandatoryFieldErrorCount > 0 && optionalFieldErrorCount > 0) {
          //form is not pristine, one or more mandatory fields are invalid and at least 1 optional field is invalid
          formInstructions.report = {
            title: "More to do...",
            info:
              "Please fill out required fields: " +
              mandatoryFieldErrorCount +
              ", optional fields needing correction: " +
              optionalFieldErrorCount +
              '. Required fields are marked with * and screen reader users will hear "Required Field". Missing required fields will show a red warning message.',
          };
        } else if (
          mandatoryFieldErrorCount === mandatoryFieldCount &&
          optionalFieldErrorCount === 0
        ) {
          //form is not pristine, no mandatory fields are valid and no optional fields are invalid
          formInstructions.report = {
            title: "Information required",
            info:
              "There are at least " +
              mandatoryFieldCount +
              " required fields which need to be filled out before we can accept your application. After you start filling out the form any invalid required fields will show a red warning message.",
          };
        } else if (
          mandatoryFieldErrorCount > 0 &&
          optionalFieldErrorCount === 0
        ) {
          //no invalid optional fields and at least one invalid mandatory field
          if (mandatoryFieldErrorCount === 1) {
            formInstructions.report = {
              title: "More to do...",
              info: "One required field needs to be completed before we can accept your application. Scroll up to find the invalid field, it  will show a red warning message.",
            };
          } else {
            formInstructions.report = {
              title: "More to do...",
              info:
                "A minimum of " +
                mandatoryFieldErrorCount +
                ' more required fields must be filled out before we can accept your application. Required fields are marked with * and screen reader users will hear "Required Field". Scroll up to find the required fields that have been missed. The missing fields  will show a red warning message.',
            };
          }
        } else if (
          mandatoryFieldErrorCount === 0 &&
          optionalFieldErrorCount > 0
        ) {
          //no invalid mandatory fields and at least one invalid optional field
          if (optionalFieldErrorCount === 1) {
            formInstructions.report = {
              title: "More to do...",
              info: "One optional field needs to be corrected before we can accept your application. Scroll up to find the invalid field.",
            };
          } else if (optionalFieldErrorCount > 1) {
            formInstructions.report = {
              title: "More to do...",
              info:
                optionalFieldErrorCount +
                " optional fields need to be corrected before we can accept your application. Scroll up to find the invalid fields.",
            };
          }
        } else if (
          mandatoryFieldErrorCount === 0 &&
          optionalFieldErrorCount === 0
        ) {
          //no invalid mandatory fields and no invalid optional fields
          formInstructions.report = {
            title: "Ready to send",
            info: 'Click "Submit" to send us your application for HPLS assistance',
          };
        }
      } else {
        formInstructions.report = {
          title: "Please start filling out the form",
          info: `There are at least ${mandatoryFieldCount} required fields which need to be filled out before we can accept your application. After you start filling out the form missing required fields will show a red message badge.`,
        };
      }

      return (
        //maxWidth={containerMW} py={10} px={6} border='0px' bg='piacgray.300'
        <Container
          as="div"
          id="hpls-form-final-instructions"
          w="100%"
          maxW={containerMW}
          p={0}
          m={0}
        >
          <Heading
            as="h3"
            fontSize={{ base: "1.6rem", md: "1.4rem", lg: "1.5rem" }}
            mt="6"
            mb={8}
          >
            {formInstructions.report.title}{" "}
          </Heading>
          <Text mt={2} fontSize="lg">
            {formInstructions.report.info}
          </Text>
          {/* <Text mt={2} fontSize='lg' >{checkButtonClicked ? 'checkButtonClicked' : 'not checking'}</Text> */}
        </Container>
      );
    }
  };

  const ProcessForm = async (event) => {
    // console.log('EVENT: ',event)
    // console.log('FORM: ',event.form)
    // console.log('event.form.getState().values: ',event.form.getState().values)
    // console.log('event.form.getState().values.hasOwnProperty("organization_key"): ',event.form.getState().values.hasOwnProperty("organization_key"))
    //const postType = 'htmlGhost' //axiosInstance //axiosDefault //fetchApi //htmlGhost

    const formEventValues = event.form.getState().values;
    var formPostValues = {};
    // eslint-disable-next-line
    var formPostResult = { title: "Submitting...", info: "Please wait" };
    var submitReady = false;

    //VALUES TO SEND
    for (const key in formEventValues) {
      if (formEventValues.hasOwnProperty(key)) {
        //console.log(`${key}: ${formEventValues[key]}`)
        if (key === "dc118__Problem1") {
          formPostValues["dc118__Problem1[]"] = Array.from(
            formEventValues[key]
          );
        } else if (key === "dc217__SafeContact") {
          formPostValues["dc217__SafeContact[]"] = Array.from(
            formEventValues[key]
          );
        } else if (
          formEventValues[key] !== null &&
          (key !== "dc118__Problem1" || key !== "dc118__Problem1[]") &&
          (key !== "dc217__SafeContact" || key !== "dc217__SafeContact[]") &&
          //key.startsWith('dc75__') || //previously used for timestamp field. There is a chance this will be used again
          (key.startsWith("dc118__") ||
            key.startsWith("dc217__") ||
            key.startsWith("p27__") ||
            key.startsWith("p62__") ||
            key === "organization_key" ||
            key === "uid")
        ) {
          // console.log(`${key}: ${formEventValues[key]}`)
          formPostValues[key] = formEventValues[key];
        }
      }
    }

    //NSW Police selected scenario - other side individual or organisation value must "T" for AS to accept
    //formPostValues['p62__company_flag'] must be changed to "company" and p62__company_name must be populated with NSW Police
    if (formPostValues["p62__company_flag"] === "NSW Police") {
      formPostValues["p62__company_flag"] = "T";
      formPostValues["p62__company_name"] = "NSW Police";
    }

    //create and populate ghost HTML form
    //action: "https://httpdump.app/dumps/85ad8d28-532c-4f20-bf9a-30171dd9b129",
    const config = {
      formId: "ActionStepWebform4",
      formName: "ActionStepWebform4",
      action: "https://go.actionstep.com/frontend/application/webform/post",
    };

    // Create the form element
    const form = document.createElement("form");
    form.id = config.formId;
    form.name = config.formName;
    form.enctype = "application/x-www-form-urlencoded";
    form.method = "post";
    form.action = config.action;

    AS_FORM_FIELDS.forEach((field) => {
      if (field !== "dc118__Problem1[]" && field !== "dc217__SafeContact[]") {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = field;
        input.id = field;
        input.value = formPostValues[field] || "";
        form.appendChild(input);
        //console.log(field, "=", formPostValues[field]);
      } else if (
        field === "dc118__Problem1[]" ||
        field === "dc118__Problem1" ||
        field === "dc217__SafeContact[]" ||
        field === "dc217__SafeContact"
      ) {
        //console.log("MULTI SELECT FIELD: ", field);
        const multiSelectCtrl = document.createElement("select");
        multiSelectCtrl.name = field;
        multiSelectCtrl.id = field;
        multiSelectCtrl.multiple = true;
        multiSelectCtrl.style.display = "none";

        //if formPostValues[inputFieldName] is defined and is an array, populate the multi select control options
        if (
          formPostValues[field] !== undefined &&
          Array.isArray(formPostValues[field])
        ) {
          formPostValues[field].map((item, i) => {
            let opt = document.createElement("option");
            opt.value = item;
            opt.innerHTML = item;
            multiSelectCtrl.append(opt);
            opt.setAttribute("selected", "selected");
            return opt;
          });
        }
        form.append(multiSelectCtrl);
      }
    });

    // Create submit button
    const submitButton = document.createElement("button");
    submitButton.type = "submit";
    submitButton.name = "Submit";
    submitButton.value = "Submit";
    submitButton.style.display = "none";
    form.appendChild(submitButton);

    const sendFormEvent = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true,
    });

    // Append the form to the body
    document.body.appendChild(form);

    submitReady = true;

    //POST TO ACTIONSTEP - HTML GHOST VERSION
    if (submitReady) {
      try {
        //console.log('ASForm.submit()')
        setTimeout(() => {
          submitButton.dispatchEvent(sendFormEvent);
          //document.getElementById(form.name).click();
          //form.submit();
        }, 10);
      } catch (error) {
        alert(`Error: ${error}`);
      }
      //delete form after submission
      setTimeout(() => {
        form.remove();
      }, 1000);
    }
  };

  // Aria Datepicker Adapter
  const DatePickerAdapter = ({
    name,
    minDate,
    maxDate,
    defDate,
    input,
    input: { onChange, value },
    ...rest
  }) => (
    <DatePicker
      name={name}
      selected={value}
      onChange={(date) => {
        onChange(date);
      }}
      {...rest}
      minValue={minDate}
      maxValue={maxDate}
      // defaultValue={today}
    />
  );

  const SelectControl = ({
    name,
    label,
    ariaLabel,
    placeholder,
    reqmesg,
    width,
    marginTop,
    opt,
  }) => {
    //const { input, meta } = useField(name)
    const { input } = useField(name);
    const mt = !marginTop ? 10 : marginTop; //default margin top value - still required?
    const isRequired = reqmesg.length > 0 ? true : false;

    return (
      <>
        <FormLabel mt={mt} htmlFor={name}>
          <Text
            as="h5"
            fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
            fontWeight={[800, 600]}
            mt={6}
            mb={-2}
          >
            {label}
            <Box as="span" color="red">
              {reqmesg}
            </Box>
          </Text>
        </FormLabel>
        <Select
          name={name}
          aria-label={ariaLabel}
          component="select"
          {...input}
          //isInvalid={meta.error && meta.touched}
          id={name}
          placeholder={placeholder}
          reqmesg={reqmesg}
          //bg={meta.error && meta.touched ? 'piacred.100' : 'white'}
          w={width}
        >
          $
          {Object.entries(opt).map(([key, value]) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </Select>

        {/* <Error name={name} show={renderToggle} isRequired={isRequired} /> */}
        {showError(useForm(), name, renderToggle, isRequired)}
      </>
    );
  };

  const SelectDayControl = ({
    name,
    stateName,
    ariaLabel,
    placeholder,
    reqmesg,
    width,
    marginTop,
    opt,
  }) => {
    const { input, meta } = useField(name);
    //const mt = (!marginTop) ? 10 : marginTop

    //on first download or without both month and year values, opt should be set to 31 days
    var idx = dateInputData.findIndex((i) => i.id === stateName);

    return (
      <>
        <Select
          name={name}
          aria-label={ariaLabel}
          component="select"
          {...input}
          isInvalid={meta.error && meta.touched}
          id={name}
          placeholder={placeholder}
          reqmesg={reqmesg}
          bg={meta.error && meta.touched ? "piacred.100" : "white"}
          w={width}
        >
          $
          {Object.entries(opt).map(([key, value]) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </Select>
        <OnChange name={name}>
          {(value, previous) => {
            if (value !== previous) {
              handleDateInputData(
                stateName,
                value,
                dateInputData[idx].month,
                dateInputData[idx].year
              );
            }
          }}
        </OnChange>
      </>
    );
  };
  const SelectMonthControl = ({
    name,
    stateName,
    ariaLabel,
    placeholder,
    reqmesg,
    width,
    marginTop,
    opt,
  }) => {
    //month
    const { input, meta } = useField(name);
    //const mt = (!marginTop) ? 10 : marginTop
    var idx = dateInputData.findIndex((i) => i.id === stateName);

    return (
      <>
        <Select
          name={name}
          aria-label={ariaLabel}
          component="select"
          {...input}
          isInvalid={meta.error && meta.touched}
          id={name}
          placeholder={placeholder}
          reqmesg={reqmesg}
          bg={meta.error && meta.touched ? "piacred.100" : "white"}
          w={width}
        >
          $
          {Object.entries(opt).map(([key, value]) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </Select>
        <OnChange name={name}>
          {(value, previous) => {
            if (value !== previous) {
              handleDateInputData(
                stateName,
                dateInputData[idx].day,
                value,
                dateInputData[idx].year
              );
            }
          }}
        </OnChange>
      </>
    );
  };
  const SelectYearControl = ({
    name,
    stateName,
    ariaLabel,
    placeholder,
    reqmesg,
    width,
    marginTop,
    opt,
  }) => {
    const { input, meta } = useField(name);
    //const mt = (!marginTop) ? 10 : marginTop
    var idx = dateInputData.findIndex((i) => i.id === stateName);
    return (
      <>
        <Select
          name={name}
          aria-label={ariaLabel}
          component="select"
          {...input}
          isInvalid={meta.error && meta.touched}
          id={name}
          placeholder={placeholder}
          reqmesg={reqmesg}
          bg={meta.error && meta.touched ? "piacred.100" : "white"}
          w={width}
        >
          $
          {Object.entries(opt).map(([key, value]) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </Select>
        <OnChange name={name}>
          {(value, previous) => {
            //if (value !== 'Year' && value !== previous) {
            if (value !== previous) {
              handleDateInputData(
                stateName,
                dateInputData[idx].day,
                dateInputData[idx].month,
                value
              );
            }
          }}
        </OnChange>
      </>
    );
  };
  const SelectCountryControl = ({
    name,
    label,
    placeholder,
    reqmesg,
    width,
    marginTop,
    opt,
  }) => {
    const { input, meta } = useField(name);
    const mt = !marginTop ? 10 : marginTop;
    const isRequired = reqmesg.length > 0 ? true : false;

    return (
      <>
        <FormLabel mt={mt} htmlFor={name}>
          <Text
            as="h5"
            fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
            fontWeight={[800, 600]}
            mt={6}
            mb={-2}
          >
            {label}
            <Box as="span" color="red">
              {reqmesg}
            </Box>
          </Text>
        </FormLabel>
        <Select
          name={name}
          aria-label="Select country of birth"
          component="select"
          {...input}
          isInvalid={meta.error && meta.touched}
          id={name}
          placeholder={placeholder}
          reqmesg={reqmesg}
          bg={meta.error && meta.touched ? "piacred.100" : "white"}
          w={width}
        >
          $
          {Object.entries(opt).map(([key, value]) => (
            <option key={key} value={value}>
              {value}
            </option>
          ))}
        </Select>

        {/* <Error name={name} show={renderToggle} isRequired={false} /> */}
        {showError(useForm(), name, renderToggle, isRequired)}
      </>
    );
  };

  const InputControl = ({
    name,
    ariaLabel,
    label,
    placeholder,
    reqmesg,
    width,
    inputType,
    marginTop,
    autoComplete,
  }) => {
    //const { input, meta } = useField(name, { subscription: { value: true, error: true, touched: true } })
    const { input } = useField(name, {
      subscription: { value: true, error: true, touched: true },
    });
    const mt = !marginTop ? 10 : marginTop;
    const ipt = !inputType ? null : inputType;
    const autocomplete = !autoComplete ? "false" : autoComplete;
    const optAriaLabel = !ariaLabel ? label : ariaLabel;
    const optPlaceholder = !placeholder ? null : placeholder;
    const isRequired = !reqmesg ? "false" : "true";

    return (
      <Control
        as={SimpleGrid}
        rows={{ base: 2, lg: 4 }}
        name={name}
        my={4}
        w={width}
      >
        <FormLabel mt={mt} htmlFor={name}>
          <Text
            as="h5"
            fontSize={{ base: "1rem", lg: "1rem" }}
            fontWeight={[800, 600]}
            mt={6}
            mb={-2}
          >
            {label}
            <Box as="span" color="red">
              {reqmesg}
            </Box>
          </Text>
        </FormLabel>
        <Input
          {...input}
          //isInvalid={meta.error && meta.touched}
          id={name}
          type={ipt}
          aria-label={optAriaLabel}
          aria-required={isRequired}
          placeholder={optPlaceholder}
          autoComplete={autocomplete}
          //bg={meta.error && meta.touched ? '#f5d1cd' : 'white'}
          color="#202025"
          borderWidth={1}
          borderColor="#cdcdcd"
          errorBorderColor="#cdcdcd"
          //focusBorderColor='focusBorderColor'
        />

        {showError(useForm(), name, renderToggle, isRequired)}
      </Control>
    );
  };

  const MultiSelectControl = ({ name, label, reqmesg, marginTop, opt }) => {
    const { input, meta } = useField(name);
    const mt = !marginTop ? 10 : marginTop;
    const isRequired = !reqmesg ? false : true;

    // Move state update to useEffect
    useEffect(() => {
      if (meta.dirty && meta.length === 0) {
        updateRenderToggle(true);
      }
    }, [meta.dirty, meta.length]);

    return (
      <>
        <FormLabel mt={mt} htmlFor={name}>
          <Text
            as="h5"
            fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
            fontWeight={[800, 600]}
            mt={6}
            mb={-2}
          >
            {label}
            <Box as="span" color="red">
              {reqmesg}
            </Box>
          </Text>
        </FormLabel>
        <ErrorBoundary>
          <ReactSelect
            //defaultValue={}
            isMulti
            name={name}
            options={opt}
            className="basic-multi-select"
            classNamePrefix="select"
            aria-label={label}
            component="react-select"
            {...input}
            isInvalid={meta.visited && meta.length === 0}
            id={name}
            reqmesg={reqmesg}
          />
        </ErrorBoundary>
        <ErrorBoundary>
          {showError(useForm(), name, renderToggle, isRequired)}
        </ErrorBoundary>
      </>
    );
  };

  const Control = ({ name, ...rest }) => {
    const {
      meta: { error, touched },
    } = useField(name, { subscription: { touched: true, error: true } });
    return <FormControl {...rest} isInvalid={error && touched} />;
  };

  const CheckboxArrayControl = ({ name, ariaLabel, value, children }) => {
    const {
      input: { checked, ...input },
      meta: { error, touched },
    } = useField(name, {
      type: "checkbox",
      value,
    });
    return (
      <Checkbox
        {...input}
        isChecked={checked}
        isInvalid={error && touched}
        colorScheme="piacred"
        size="lg"
        background="white"
        aria-label={ariaLabel}
        color=""
        p={2}
        pr={4}
      >
        {children}
      </Checkbox>
    );
  };

  const AdaptedTextarea = ({ input, meta, ...rest }) => (
    <Textarea
      {...input}
      {...rest}
      //isInvalid={meta.error && meta.touched}
      //bg={ meta.error && meta.touched ? '#f5d1cd' : 'white' }
      borderWidth={1}
      borderColor="#cdcdcd"
      errorBorderColor="#cdcdcd"
      //focusBorderColor='focusBorderColor'
    />
  );

  const TextareaControl = ({
    name,
    label,
    ariaLabel,
    placeholder,
    reqmesg,
    width,
  }) => {
    const isRequired = reqmesg && reqmesg.length > 0 ? true : false;
    return (
      <Control name={name} my={4} w="100%">
        <FormLabel mt={10} htmlFor={name}>
          <Text
            as="h5"
            fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
            fontWeight={[800, 600]}
            mt={6}
            mb={-2}
          >
            {label}
            <Box as="span" color="red">
              {reqmesg}
            </Box>
          </Text>
        </FormLabel>

        <Field
          name={name}
          component={AdaptedTextarea}
          aria-label={ariaLabel}
          placeholder={placeholder}
          id={name}
        />

        {/* <Error name={name} show={renderToggle} isRequired={isRequired } /> */}
        {showError(useForm(), name, renderToggle, isRequired)}
      </Control>
    );
  };

  const AdaptedSwitch = ({ input, meta, ...rest }) => (
    <Switch
      type="checkbox"
      {...input}
      {...rest}
      isInvalid={meta.error && meta.touched}
      //bg={ meta.error && meta.touched ? '#f5d1cd' : null }
      size="lg"
      colorScheme="piacred"
      isChecked={input.checked}
    />
  );

  const SwitchControl = ({ name, label }) => {
    return (
      <FormControl display="flex" alignItems="center" mt={[10, 4]}>
        <FormLabel htmlFor={name} mb="0">
          <Text
            as="h5"
            fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
            fontWeight={[800, 600]}
            mt={6}
            mb={-2}
          >
            {label}
          </Text>
        </FormLabel>
        <Field
          name={name}
          component={AdaptedSwitch}
          id={name}
          type="checkbox"
          mt={6}
          mb={-2}
        />
        <Error name={name} show={renderToggle} isRequired={false} />
      </FormControl>
    );
  };

  const AdaptedRadioGroup = ({ input, meta, label, children }) => (
    <FormControl isInvalid={meta.touched && meta.invalid} my={4}>
      <FormLabel htmlFor={input.name} aria-label={label}>
        <Text
          as="h5"
          fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
          fontWeight={[800, 600]}
          mt={6}
          mb={-2}
        >
          {label}
        </Text>
      </FormLabel>
      <RadioGroup {...input}>{children}</RadioGroup>
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
  const Condition = ({ when, is, children }) => (
    <Field name={when} subscription={{ value: true }}>
      {({ input: { value } }) => (value === is ? children : null)}
    </Field>
  );

  const Error = ({ name, msg, show }) => {
    //Name is the field name, msg is a custom message, show is a boolean to display the error message
    // TO DO: REFACTOR - the showError function now decides if the error message should be displayed
    const {
      meta: { error },
    } = useField(name, { subscription: { error: true } });

    let errMsg = "";
    if (error) {
      //Message supplied from validation function
      errMsg = `${error}`;
    } else if (msg && show) {
      //Field level custom message
      errMsg = `Test custom message - ${msg}`; //msg
    }

    if (errMsg && show) {
      return (
        <Text
          className="validation-error"
          bg="piacred.500"
          color="white"
          borderRadius="md"
          fontWeight="800"
          py={2}
          px={4}
          mt={2}
        >
          <WarningIcon w={4} h={4} mr={2} />
          {errMsg}
        </Text>
      );
    }
  };

  //custom error message for array controls
  const ArrayControlError = ({ name, msg, show, isRequired }) => {
    const {
      meta: { length, error },
    } = useField(name, { subscription: { length: true } });

    //TO DO: add custom error message handling for array controls ***************************

    if (show && isRequired && (length === 0 || length === undefined)) {
      return (
        <Text
          className="validation-error"
          bg="piacred.500"
          color="white"
          borderRadius="md"
          fontWeight="800"
          py={2}
          px={4}
          mt={2}
        >
          <WarningIcon w={4} h={4} mr={2} />
          {error}
        </Text>
      );
    }
  };

  return (
    <Form
      onSubmit={ProcessForm}
      validate={validate}
      enctype="application/x-www-form-urlencoded"
      initialValues={initialData}
      render={({ handleSubmit, form, submitting, pristine, values }) => (
        <Box as="form" p={0} m={0} borderWidth="0px" onSubmit={handleSubmit}>
          <HPLSInfoAccordion />

          <Box id="hpls-form-container" w="100%" bg="piacgray.300" pb={14}>
            <HPLSFormHeader />

            <Container
              maxWidth={containerMW}
              p={6}
              pb={14}
              border="1px"
              borderRadius="none"
              borderColor="#999999"
              bg="piacgray.200"
            >
              <Flex
                alignItems="center"
                justifyContent="flex-start"
                flexDirection="row"
              >
                <Box
                  id="hpls-sn-1"
                  as="span"
                  py={0.6}
                  px={2}
                  mr={2}
                  borderRadius="md"
                  borderColor="piacgray.500"
                  bg="piacgray.300"
                  borderWidth="1px"
                  color="piacgray.500"
                  aria-hidden="true"
                >
                  1
                </Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "1.2rem", md: "1.4rem", lg: "2rem" }}
                  mt="6"
                  mb={8}
                  aria-label="Section 1: Details of the person applying for help"
                >
                  Details of the person applying for help
                </Heading>
              </Flex>

              <Text
                as="h4"
                mt={10}
                fontSize={{ base: "1.1rem", md: "1.2rem", lg: "1.4rem" }}
                fontWeight={[800, 700]}
              >
                Name
              </Text>

              <InputControl
                name="p27__first_name"
                ariaLabel="Required information: Applicant first name"
                label="First Name"
                placeholder=" "
                autoComplete="given-name"
                reqmesg="*"
                width={controlWidth.lg}
                labelSize="smaller"
                marginTop={4}
              />

              <InputControl
                name="p27__middle_name"
                ariaLabel="Applicant middle name(s)"
                label="Middle Name(s)"
                placeholder=" "
                autoComplete="additional-name"
                width={controlWidth.lg}
                labelSize="smaller"
                marginTop={2}
              />

              <InputControl
                name="p27__last_name"
                ariaLabel="Required information: Applicant last name"
                label="Last Name"
                placeholder=" "
                autoComplete="family-name"
                reqmesg="*"
                width={controlWidth.lg}
                labelSize="smaller"
                marginTop={2}
              />

              <Divider mt={10} mb={14} />

              <FormLabel
                as="h4"
                mt={10}
                htmlFor="clientDOB"
                fontSize={{ base: "1.1rem", md: "1.2rem", lg: "1.4rem" }}
                lineHeight={1}
                fontWeight="bold"
              >
                Date of birth{" "}
                <Box as="span" color="red">
                  *
                </Box>
              </FormLabel>
              <Text as="h5" className="hpls-sublabel" fontSize="sm">
                Enter your date of birth using the selectors below.
              </Text>

              <SimpleGrid
                className="hpls-dob-date-group"
                columns={1}
                spacing={1}
                width="36rem"
              >
                <Stack direction={["column", "row"]} spacing="14px" mt={2}>
                  <Box w="8.1em" h="3em">
                    <SelectDayControl
                      name="clientDOB_day"
                      stateName="clientDOB"
                      ariaLabel="Select day of birth"
                      reqmesg=""
                      opt={daysInSelectedMonth(
                        dateInputData[0].year,
                        dateInputData[0].month
                      )}
                      width={controlWidth.xs}
                      labelSize="smaller"
                      placeholder="Day"
                    />
                  </Box>
                  <Box w="8.1em" h="3em">
                    <SelectMonthControl
                      name="clientDOB_month"
                      stateName="clientDOB"
                      ariaLabel="Select month of birth"
                      reqmesg=""
                      opt={CalendarMonths}
                      width={controlWidth.xs}
                      labelSize="smaller"
                      placeholder="Month"
                    />
                  </Box>
                  <Box w="8.1em" h="3em">
                    <SelectYearControl
                      name="clientDOB_year"
                      stateName="clientDOB"
                      ariaLabel="Year"
                      reqmesg=""
                      opt={dobYears(yearNow, ageRange)}
                      width={controlWidth.xs}
                      labelSize="smaller"
                      placeholder="Year"
                    />
                  </Box>
                </Stack>

                {/* <Center> */}
                <SimpleGrid
                  className="hpls-dob-feedback-panel"
                  columns={{ base: 1, md: 2 }}
                  spacing={4}
                  mt={2}
                  mx="auto"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    borderColor="piacred.200"
                  >
                    <Center
                      w="12em"
                      h="4em"
                      p={4}
                      border="1px"
                      borderRadius="md"
                      borderColor="#999999"
                      bg="piacgray.200"
                    >
                      <Text
                        id="client-dob-date-selection-display"
                        fontSize={{ base: "1rem", md: "1.2rem", lg: "1.1rem" }}
                        fontWeight={[700, 600]}
                        color={showDateWarningColor(
                          dateInputData[0].day,
                          dateInputData[0].month,
                          dateInputData[0].year
                        )}
                      >
                        {`${dateInputData[0].day}/${dateInputData[0].month}/${dateInputData[0].year}`}
                      </Text>
                    </Center>
                  </Flex>
                </SimpleGrid>
                {/* </Center> */}

                <Container w="25rem" p={0} m={0}>
                  <Error
                    name="p27__date_of_birth"
                    show={renderToggle}
                    isRequired={true}
                  />
                </Container>
              </SimpleGrid>

              <Divider mt={10} mb={14} />

              <Text
                as="h4"
                mt={10}
                fontSize={{ base: "1.1rem", md: "1.2rem", lg: "1.4rem" }}
                fontWeight={[800, 700]}
              >
                Where are you usually staying/sleeping?
              </Text>

              <SelectControl
                name="dc217__Housing"
                label="Select the closest option"
                ariaLabel="Select housing status"
                reqmesg=""
                opt={HousingStatus}
                width={controlWidth.lg}
                labelSize="smaller"
                placeholder="Please select"
              />

              <Box id="address-separator" mt={10} mb={4} />

              <Text
                as="h4"
                mt={10}
                fontSize={{ base: "1.1rem", md: "1.2rem", lg: "1.4rem" }}
                fontWeight={[800, 700]}
              >
                Address
              </Text>

              <InputControl
                name="p27__address_line_1"
                placeholder="21 George Street"
                label="Address 1"
                width={controlWidth.lg}
                marginTop={1}
              />
              <InputControl
                name="p27__address_line_2"
                label="Address 2"
                width={controlWidth.lg}
                marginTop={1}
              />
              <InputControl
                name="p27__city"
                placeholder="Sydney"
                label="Suburb"
                width={controlWidth.sm}
                marginTop={1}
              />
              <InputControl
                name="p27__post_code"
                placeholder="2000"
                label="Postcode"
                width={controlWidth.sm}
                marginTop={1}
              />

              <Divider mt={10} mb={14} />

              <Container w={controlWidth.lg} p={0} m={0}>
                <Text
                  as="h4"
                  mt={10}
                  fontSize={{ base: "1.1rem", md: "1.2rem", lg: "1.4rem" }}
                  fontWeight={[800, 700]}
                >
                  Best contact number
                </Text>
              </Container>
              <Alert
                id="hpls-info-contact"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="left"
                border="1px solid #999"
                bg="piacgray.100"
                mt={10}
                mb={10}
                pb={8}
                px={6}
                w={["100%", "80%"]}
              >
                <AlertTitle mt={4} mb={1}>
                  <Flex
                    alignItems="left"
                    justifyContent="flex-start"
                    flexDirection="row"
                  >
                    <InfoIcon
                      boxSize="28px"
                      mr={2}
                      aria-hidden="true"
                      color="piacyellow.500"
                    />
                    <Text
                      fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
                      mt={0.5}
                      fontWeight={[800, 700]}
                    >
                      If the person does not have a phone number:
                    </Text>
                  </Flex>
                </AlertTitle>
                <AlertDescription p={3}>
                  <OrderedList lineHeight="tight">
                    <ListItem>
                      You can give the number of the caseworker or support
                      person
                    </ListItem>
                    <ListItem>
                      If you cannot give any phone number, please call us on
                      8898 6500
                    </ListItem>
                  </OrderedList>
                </AlertDescription>
              </Alert>

              <Container w={controlWidth.lg} p={0} m={0}>
                <InputControl
                  name="p27__phone1"
                  ariaLabel="Required information: Enter the best contact number. This could be the number of the caseworker or support person. If you cannot give any phone number, please call us on 8898 6500."
                  label="Contact number"
                  placeholder="0000 000 000"
                  autoComplete="tel"
                  reqmesg="*"
                  width={controlWidth.lg}
                  labelSize="smaller"
                  marginTop={4}
                />

                <SelectControl
                  name="p27__phone1_description"
                  label="Phone number type"
                  ariaLabel="Required information: Select phone number type"
                  marginTop={1}
                  reqmesg="*"
                  opt={PhoneDescription}
                  width={controlWidth.lg}
                  labelSize="smaller"
                  placeholder="Please select"
                />

                <InputControl
                  name="p27__e_mail"
                  type="email"
                  label="Email address"
                  placeholder="email@domain.com"
                  autoComplete="email"
                  width={controlWidth.lg}
                  labelSize="smaller"
                  marginTop={1}
                />
              </Container>
            </Container>

            <Container
              maxWidth={containerMW}
              pt={6}
              pr={6}
              pl={6}
              pb={14}
              mt={4}
              border="1px"
              borderRadius="none"
              borderColor="#999999"
              bg="piacgray.200"
            >
              <Flex
                alignItems="center"
                justifyContent="flex-start"
                flexDirection="row"
              >
                <Box
                  id="hpls-sn-2"
                  as="span"
                  py={0.6}
                  px={2}
                  mr={2}
                  fontSize={{ base: "1.2rem", md: "1.4rem", lg: "2rem" }}
                  borderRadius="md"
                  borderColor="piacgray.500"
                  bg="piacgray.300"
                  borderWidth="1px"
                  color="piacgray.500"
                  aria-hidden="true"
                >
                  2
                </Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "1.2rem", md: "1.4rem", lg: "2rem" }}
                  mt="6"
                  mb={8}
                  aria-label="Section 2: Is this a referral?"
                >
                  Is this a referral?
                </Heading>
              </Flex>
              <Box
                textAlign="left"
                border="1px solid #999" //#A21D36
                bg="piacgray.100"
                mt={10}
                mb={4}
                py={4}
                px={6}
                w={["100%", "80%"]}
              >
                <Flex
                  id="hpls-info-referral"
                  alignItems="left"
                  justifyContent="flex-start"
                  flexDirection="row"
                >
                  <InfoIcon
                    boxSize="24px"
                    w={5}
                    h={5}
                    mt={0.5}
                    mr={3}
                    color="piacyellow.500"
                  />
                  <Text
                    fontSize={{ base: "1rem", md: "1rem", lg: "1.1rem" }}
                    fontWeight={[800, 600]}
                  >
                    If this is not a referral, go to the next section.
                  </Text>
                </Flex>
              </Box>
              <SwitchControl
                mb={12}
                name="isAgency"
                label="Is this a referral from a HPLS host agency?"
              />

              <Condition when="isAgency" is={true}>
                <SelectControl
                  name="dc118__WelfareAgencyV2"
                  label="Please select your agency"
                  ariaLabel="Please select your agency"
                  reqmesg=""
                  opt={WelfareAgency}
                  width={controlWidth.lg}
                  labelSize="md"
                  placeholder="Please Select"
                  defaultSelected="Not from a HPLS Host Agency"
                />
                <Box id="welfare-appt-separator" mt={10} mb={14} />
                <Text
                  as="h5"
                  mt={10}
                  fontSize="md"
                  lineHeight={1}
                  fontWeight="bold"
                >
                  If you have an appointment booked at an HPLS clinic, please
                  enter the time/date of booking
                </Text>

                {/* <ErrorBoundary> */}
                <Field
                  name="HplsClinicApptDate"
                  label="Clinic Appointment Date"
                  ariaLabel="Please select a clinic appointment date"
                  dateFormat="yyyy/mm/dd"
                  component={DatePickerAdapter}
                  minDate={today(getLocalTimeZone())}
                  maxDate={today(getLocalTimeZone()).add({ days: 90 })}
                  defDate={today(getLocalTimeZone())}
                />
                {/* </ErrorBoundary> */}

                <SelectControl
                  name="HplsClinicApptTime"
                  label="Clinic Appointment Time"
                  ariaLabel="Please select a clinic appointment time"
                  reqmesg=""
                  opt={AppointmentTimes}
                  width={controlWidth.lg}
                  defaultSelected=""
                  placeholder="Please select"
                />
              </Condition>

              <Condition when="isAgency" is={false}>
                <SwitchControl
                  name="wasReferred"
                  label="Is this a referral from someone else?"
                  labelSize="md"
                />

                <Condition when="wasReferred" is={true}>
                  <InputControl
                    name="dc118__ReferralOrg"
                    ariaLabel="Referred by"
                    label="Referred by"
                    placeholder=""
                    width={controlWidth.lg}
                    labelSize="normal"
                  />
                </Condition>
              </Condition>

              <TextareaControl
                name="dc118__Caseworker"
                ariaLabel="Please give details of the person referring (name and contact details)"
                label="Person referring (name and contact details)"
                placeholder=""
                width={controlWidth.xl}
                marginTop={4}
              />
            </Container>

            <Container
              maxWidth={containerMW}
              p={6}
              mt={4}
              border="1px"
              borderRadius="none"
              borderColor="#999999"
              bg="piacgray.200"
            >
              <Flex
                alignItems="center"
                justifyContent="flex-start"
                flexDirection="row"
              >
                <Box
                  id="hpls-sn-3"
                  as="span"
                  py={0.6}
                  px={2}
                  mr={2}
                  borderRadius="md"
                  borderColor="piacgray.500"
                  bg="piacgray.300"
                  borderWidth="1px"
                  color="piacgray.500"
                  aria-hidden="true"
                >
                  3
                </Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "1.2rem", md: "1.4rem", lg: "2rem" }}
                  mt="6"
                  mb={8}
                  aria-label="Section 3: Information about the person needing help"
                >
                  Information about the person needing help
                </Heading>
              </Flex>
              <Box
                textAlign="left"
                border="1px solid #999" //#A21D36
                bg="piacgray.100"
                mt={10}
                mb={4}
                py={4}
                px={6}
                w={["100%", "80%"]}
              >
                <Flex
                  id="hpls-info-case-help"
                  alignItems="left"
                  justifyContent="flex-start"
                  flexDirection="row"
                >
                  <InfoIcon
                    boxSize="24px"
                    w={5}
                    h={5}
                    mt={0.5}
                    mr={3}
                    color="piacyellow.500"
                  />
                  <Text
                    fontSize={{ base: "1rem", md: "1rem", lg: "1.1rem" }}
                    fontWeight={[800, 600]}
                  >
                    This information can help us with the case.
                  </Text>
                </Flex>
              </Box>
              {/* <Text my={6}>This information can help us with the case.</Text> */}
              <SelectCountryControl
                name="p27__64__CountryOfBirth"
                label="Country of birth?"
                reqmesg=""
                opt={countryNames}
                width={controlWidth.lg}
                labelSize="normal"
                placeholder="Please select"
                defaultValue="Please select"
              />

              <SelectControl
                name="p27__gender"
                label="Gender identity"
                ariaLabel="Please state gender identity"
                reqmesg=""
                opt={GenderIdentity}
                width={controlWidth.lg}
                labelSize="normal"
                placeholder="Please select"
              />

              <SelectControl
                name="p27__class_aboriginal_status"
                label="Aboriginal and/or Torres Strait Islander?"
                ariaLabel="Are you Aboriginal and, or, Torres Strait Islander?"
                reqmesg=""
                opt={AboriginalTorresStrait}
                width={controlWidth.lg}
                labelSize="normal"
                placeholder="Please select"
              />

              <Divider id="safe-contact-divider" mt={10} mb={14} />

              <Control name="safe_contact_list" my={4} value="">
                {" "}
                <br />
                <FormLabel
                  as="h4"
                  mt={10}
                  htmlFor="safe_contact_list"
                  fontSize="normal"
                  lineHeight={1}
                  fontWeight="bold"
                >
                  Is it safe to contact via phone, text, email?
                </FormLabel>
                <Text as="h5" className="hpls-sublabel">
                  I give permission for the Homeless Person's Legal Service to:
                </Text>
                <SimpleGrid mt={2} mb={1} columns={[1, 3]} spacing={0.8}>
                  <CheckboxArrayControl
                    name="safe_contact_list"
                    ariaLabel="Call me"
                    value="I give permission for HPLS staff to CALL me"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      CALL
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="safe_contact_list"
                    ariaLabel="Text me"
                    value="I give permission for HPLS staff to TEXT me"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      TEXT
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="safe_contact_list"
                    ariaLabel="Email me"
                    value="I give permission for HPLS staff to EMAIL me"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      EMAIL
                    </Text>
                  </CheckboxArrayControl>
                  <Text className="hpls-note">(Check all that apply)</Text>
                </SimpleGrid>
              </Control>

              <Divider mt={10} mb={14} />

              <Control name="disabilities_list" my={4} value="">
                <FormLabel
                  as="h4"
                  mt={10}
                  htmlFor="disabilities_list"
                  fontSize="normal"
                  lineHeight={1}
                  fontWeight="bold"
                >
                  Are you living with a disability?
                </FormLabel>
                <Text className="hpls-sublabel" as="h5">
                  If yes, what type of disability? Please select up to 4.
                </Text>
                <SimpleGrid mt={2} mb={1} columns={[1, 2]} spacing={0.8}>
                  <CheckboxArrayControl
                    name="disabilities_list"
                    ariaLabel="Optionally, select up to 4 disabilities: option 1: physical"
                    value="Physical"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      Physical
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="disabilities_list"
                    ariaLabel="Optionally, select up to 4 disabilities: option 2: Intellectual"
                    value="Intellectual"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      Intellectual
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="disabilities_list"
                    ariaLabel="Optionally, select up to 4 disabilities: option 3: Mental Illness"
                    value="Mental Illness"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      Mental Illness
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="disabilities_list"
                    ariaLabel="Optionally, select up to 4 disabilities: option 4: Drug Dependence"
                    value="Drug Dependence"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      Drug Dependence
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="disabilities_list"
                    ariaLabel="Optionally, select up to 4 disabilities: option 5: Neurological"
                    value="Neurological"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      Neurological
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="disabilities_list"
                    ariaLabel="Optionally, select up to 4 disabilities: option 6: Sensory"
                    value="Sensory"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      Sensory (sight, hearing, smell, touch, taste, or spatial
                      awareness)
                    </Text>
                  </CheckboxArrayControl>
                  <CheckboxArrayControl
                    name="disabilities_list"
                    ariaLabel="Optionally, select up to 4 disabilities: option 7: Not Disclosed"
                    value="Not Disclosed"
                  >
                    <Text
                      fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                      fontWeight={[700, 500]}
                    >
                      Not Disclosed
                    </Text>
                  </CheckboxArrayControl>
                </SimpleGrid>
              </Control>
              <Error
                name="disabilities_list"
                show={renderToggle}
                isRequired={false}
              />
              <Divider mt={10} mb={14} />

              <SelectControl
                name="dc217__IncomeSource"
                label="Source of income?"
                ariaLabel="Please select a source of income"
                reqmesg=""
                opt={incomeSources}
                width={controlWidth.lg}
                labelSize="normal"
                placeholder="Please select"
              />

              <Divider mt={10} mb={14} />

              <Box mt={10} mb={14}>
                <SelectControl
                  name="p27__class_primary_language"
                  label="Preferred language"
                  ariaLabel="Please select a preferred language"
                  reqmesg=""
                  opt={PreferredLanguage}
                  width={controlWidth.lg}
                  labelSize="normal"
                  placeholder="Please select"
                />
                <Error name="p27__class_primary_language" show={false} />

                <SwitchControl
                  name="interpreter_required"
                  label="Do they need an interpreter?"
                  labelSize="md"
                />
              </Box>
            </Container>

            <Container
              maxWidth={containerMW}
              p={6}
              pb={12}
              mt={4}
              border="1px"
              borderRadius="none"
              borderColor="#999999"
              bg="piacgray.200"
            >
              <Flex
                alignItems="center"
                justifyContent="flex-start"
                flexDirection="row"
              >
                <Box
                  id="hpls-sn-4"
                  as="span"
                  py={0.6}
                  px={2}
                  mr={2}
                  borderRadius="md"
                  borderColor="piacgray.500"
                  bg="piacgray.300"
                  borderWidth="1px"
                  color="piacgray.500"
                  aria-hidden="true"
                >
                  4
                </Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "1.2rem", md: "1.4rem", lg: "2rem" }}
                  mt="6"
                  mb={8}
                  aria-label="Section 4: The legal issue"
                >
                  The legal issue
                </Heading>
              </Flex>

              <MultiSelectControl
                name="dc118__Problems"
                opt={LEGAL_ISSUES}
                my={4}
                label="Please select one or more problems"
                ariaLabel="Please select one or more problems"
                reqmesg="*"
                width={controlWidth.lg}
              ></MultiSelectControl>
              <Error
                name="dc118__Problem1"
                show={renderToggle}
                isRequired={true}
              />
              <strong>
                <ArrayControlError
                  name="dc118__Problem1"
                  msg="Please select at least one legal issue"
                  show={renderToggle}
                />
              </strong>

              <TextareaControl
                name="dc118__InstructionsMatterDescription"
                ariaLabel="Required information: Describe the legal issue. If it is a criminal issue, please include: The court where your matter is being heard. Your next court date. The number of times you have been to court for this matter, and a brief description of the charges."
                label="Description"
                placeholder="Please include as much detail as you can, such as who is involved, when the legal issue started and what has happened so far."
                reqmesg="*"
                width={controlWidth.lg}
              />
              {/* <strong><Error name="dc118__InstructionsMatterDescription" /></strong> */}

              <TextareaControl
                name="dc118__CriminalMatter"
                ariaLabel="If this is a criminal issue, please include description of charge, criminal charge reference number, court and court date."
                label="If this is a criminal issue, please include description of charge, criminal charge reference number, court and court date."
                placeholder="Please include as much detail as you can"
                width={controlWidth.lg}
              />
              <strong>
                <Error name="dc118__CriminalMatter" show={false} />
              </strong>

              <Divider mt={10} mb={14} />

              <Box mt={3} mb={4}>
                <Text
                  as="h4"
                  mt={10}
                  fontSize={{ base: "1.3rem", md: "1.4rem", lg: "1.5rem" }}
                  fontWeight={600}
                >
                  Who is the legal issue with?
                </Text>

                <Field
                  name="p62__company_flag"
                  component={AdaptedRadioGroup}
                  label="Is your issue with an organisation, or an individual person?"
                >
                  <SimpleGrid columns={[1, 4]} spacing={1}>
                    <Radio bg="white" size="lg" value="F" colorScheme="piacred">
                      <Text
                        aria-label="Select an option to describe who the legal issue is with. Option 1 of 3: an individual person"
                        fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                        fontWeight={[700, 500]}
                      >
                        Individual person
                      </Text>
                    </Radio>
                    <Radio bg="white" size="lg" value="T" colorScheme="piacred">
                      <Text
                        aria-label="Select an option to describe who the legal issue is with. Option 2 of 3: an organisation"
                        fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                        fontWeight={[700, 500]}
                      >
                        Organisation
                      </Text>
                    </Radio>
                    <Radio
                      bg="white"
                      size="lg"
                      value="NSW Police"
                      colorScheme="piacred"
                    >
                      <Text
                        aria-label="Select an option to describe who the legal issue is with. Option 3 of 3: NSW Police"
                        fontSize={{ base: "1.1rem", md: "0.9.rem", lg: "1rem" }}
                        fontWeight={[700, 500]}
                      >
                        NSW Police
                      </Text>
                    </Radio>
                  </SimpleGrid>
                </Field>
                <Error name="p62__company_flag" show={false} />
              </Box>

              <Condition when="p62__company_flag" is="F">
                <InputControl
                  name="p62__first_name"
                  ariaLabel="Other party first name"
                  label="First name"
                  placeholder=""
                  width={controlWidth.lg}
                  labelSize="smaller"
                />
                <InputControl
                  name="p62__middle_name"
                  ariaLabel="Other party middle name"
                  label="Middle name"
                  placeholder=""
                  width={controlWidth.lg}
                  labelSize="smaller"
                  marginTop="2"
                />
                <InputControl
                  name="p62__last_name"
                  ariaLabel="Other party last name"
                  label="Last name"
                  placeholder=""
                  width={controlWidth.lg}
                  labelSize="smaller"
                  marginTop="2"
                />

                <FormLabel
                  mt={10}
                  htmlFor="otherSideDOB"
                  fontSize={{ base: "1rem", md: "1.1rem", lg: "1.2rem" }}
                  lineHeight={1}
                  fontWeight="bold"
                >
                  Other side date of birth
                </FormLabel>
                <Text className="hpls-sublabel" fontSize="sm">
                  Enter the person's date of birth using the selectors below.
                </Text>

                <SimpleGrid
                  className="hpls-dob-date-group"
                  columns={1}
                  spacing={1}
                  width="36rem"
                >
                  <Stack direction={["column", "row"]} spacing="14px" mt={2}>
                    <Box w="8.1em" h="3em">
                      <SelectDayControl
                        name="otherSideDOB_day"
                        stateName="otherSideDOB"
                        ariaLabel="Select day of birth"
                        reqmesg=""
                        opt={daysInSelectedMonth(
                          dateInputData[1].year,
                          dateInputData[1].month
                        )}
                        width={controlWidth.xs}
                        labelSize="smaller"
                        placeholder="Day"
                      />
                    </Box>
                    <Box w="8.1em" h="3em">
                      <SelectMonthControl
                        name="otherSideDOB_month"
                        stateName="otherSideDOB"
                        ariaLabel="Select month of birth"
                        reqmesg=""
                        opt={CalendarMonths}
                        width={controlWidth.xs}
                        labelSize="smaller"
                        placeholder="Month"
                      />
                    </Box>
                    <Box w="8.1em" h="3em">
                      <SelectYearControl
                        name="otherSideDOB_year"
                        stateName="otherSideDOB"
                        ariaLabel="Year"
                        reqmesg=""
                        opt={dobYears(yearNow, ageRange)}
                        width={controlWidth.xs}
                        labelSize="smaller"
                        placeholder="Year"
                      />
                    </Box>
                  </Stack>

                  {/* <Center> */}
                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    spacing={4}
                    mt={2}
                    mx="auto"
                  >
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      borderColor="piacred.200"
                    >
                      <Center
                        w="12em"
                        h="4em"
                        p={4}
                        border="1px"
                        borderRadius="md"
                        borderColor="#999999"
                        bg="piacgray.200"
                      >
                        <Text
                          id="client-dob-date-selection-display"
                          fontSize={{
                            base: "1rem",
                            md: "1.2rem",
                            lg: "1.1rem",
                          }}
                          fontWeight={[700, 600]}
                          color={showDateWarningColor(
                            dateInputData[0].day,
                            dateInputData[0].month,
                            dateInputData[0].year
                          )}
                        >
                          {`${dateInputData[1].day}/${dateInputData[1].month}/${dateInputData[1].year}`}
                        </Text>
                      </Center>
                    </Flex>
                  </SimpleGrid>

                  {!dateInputData[1].is_full_date ? (
                    <Center maxW="24em" mt={2}>
                      <Text
                        fontSize={{ base: "1rem", md: "1.1rem", lg: "1.1rem" }}
                        fontWeight={[700, 600]}
                        color={showDateWarningColor(
                          dateInputData[1].day,
                          dateInputData[1].month,
                          dateInputData[1].year
                        )}
                      >
                        Please enter a valid date of birth
                      </Text>
                    </Center>
                  ) : (
                    ""
                  )}

                  {/* The Error component is not placed here since this is not a required field. Should an error warning message be needed then it must be added. Refer to client date of birth for method */}
                </SimpleGrid>
              </Condition>

              <Condition when="p62__company_flag" is="T">
                <InputControl
                  name="p62__company_name"
                  ariaLabel="Other party organisation name"
                  label="Name of the organisation"
                  placeholder=""
                  width={controlWidth.lg}
                  labelSize="md"
                />
              </Condition>

              <Divider mt={14} mb={14} />
              <Box mt={3} mb={14}>
                <Text
                  as="h4"
                  mt={10}
                  fontSize={{ base: "1.3rem", md: "1.4rem", lg: "1.5rem" }}
                  fontWeight={600}
                >
                  Other parties
                </Text>

                <SwitchControl
                  name="otherDisputeParties"
                  label="Are there any other organisations or people involved in the legal issue?"
                  labelSize="md"
                />

                <Condition when="otherDisputeParties" is={true}>
                  <Box>
                    <TextareaControl
                      name="dc118__WebformOtherParties"
                      ariaLabel="Please list other organisations or people involved in the legal issue."
                      label="Other party details"
                      placeholder="Additional parties involved are ..."
                      width={controlWidth.lg}
                    />
                    <Error name="WebformOtherParties" show={false} />
                  </Box>
                </Condition>
              </Box>
            </Container>

            <Container
              maxWidth={containerMW}
              p={6}
              mt={4}
              border="1px"
              borderRadius="none"
              borderColor="#999999"
              bg="piacgray.200"
            >
              <ErrorBoundary>
                <ProcessFormVars />
                <DisplayFormInstructions />
              </ErrorBoundary>
              <ButtonGroup
                id="check-submit-reset-button-group"
                spacing={4}
                mb={10}
                mt={10}
              >
                <Button
                  variant="solid"
                  value={true}
                  onClick={() => {
                    updateCheckButtonClicked(true);
                    updateRenderToggle(true);
                  }}
                  isDisabled={
                    pristine ||
                    (form.getState().errors.mandatoryFieldErrorCount === 0 &&
                      form.getState().errors.optionalFieldErrorCount === 0)
                  } //warning count key is always present //|| Object.keys(form.getState().errors).length <= 1 - not longer needed
                  bg="piacyellow.500"
                  border="1px solid black"
                  color="black"
                  rounded="none"
                  _hover={{
                    bg:
                      pristine || submitting ? "piacyellow.500" : "piacred.500",
                    color: pristine || submitting ? "black" : "white",
                  }}
                >
                  Check
                </Button>

                <ErrorBoundary>
                  <Button
                    isLoading={submitting}
                    loadingText="Submitting"
                    variant="solid"
                    type="submit"
                    form="HPLSForm"
                    isDisabled={
                      pristine ||
                      submitting ||
                      form.getState().errors.mandatoryFieldErrorCount > 0 ||
                      form.getState().errors.optionalFieldErrorCount > 0
                    }
                    onClick={async (event) => {
                      await ProcessForm({ event, form });
                    }}
                    bg="black"
                    border="1px solid black"
                    color="white"
                    rounded="none"
                    _hover={{
                      bg:
                        pristine ||
                        submitting ||
                        form.getState().errors.mandatoryFieldErrorCount > 0 ||
                        form.getState().errors.optionalFieldErrorCount > 0
                          ? "black"
                          : "piacred.500",
                    }}
                  >
                    Submit
                  </Button>
                </ErrorBoundary>

                <ErrorBoundary>
                  <Button
                    variant="solid"
                    onClick={(e) => {
                      e.preventDefault();
                      form.reset();
                      handleResetButtonClicked();
                    }}
                    isDisabled={submitting || pristine}
                    bg="piacyellow.500"
                    border="1px solid black"
                    color="black"
                    rounded="none"
                    _hover={{
                      bg:
                        pristine || submitting
                          ? "piacyellow.500"
                          : "piacred.500",
                      color: pristine || submitting ? "black" : "white",
                    }}
                  >
                    Reset
                  </Button>
                </ErrorBoundary>
              </ButtonGroup>
            </Container>
          </Box>

          {/* <Box w="80%" p={4} mt={10} mb={0} borderWidth='4px' fontSize="0.8rem" borderRadius='lg' borderColor='gray' bg='gray.100'>

            <Heading size='md' mb={2}>Test Form Dynamic Status Report</Heading>
            <Text>(This will be removed for the production version)</Text>
            <Badge variant='solid' colorScheme='green' p={2} mt={3} mr={1} mb={3}>
              Pristine: {JSON.stringify(pristine, 0, 2)}
            </Badge>

            <Heading size='sm' mt={2}>Mandatory Conditions</Heading>
            <Text>Count: {Object.keys(errors).length}</Text>
            <Box as="pre">
              {JSON.stringify(errors, 0, 2)}
            </Box>
   
            <Heading size='sm'>Form Values</Heading>
            <Box as="pre">
              {JSON.stringify(values, 0, 2)}
            </Box>

            <Heading size='sm'>State</Heading>
            <Box as="pre">
              {JSON.stringify(form.getState(), 0, 2)}
            </Box>
            <Heading size='sm'>Watch</Heading>
            <Box as="pre">
              {JSON.stringify(form.watch, 0, 2)}
            </Box>

          </Box> */}
        </Box>
      )}
    />
  );
}

export default App;
